// Labels for values that arrive as DATA rather than as source strings.
//
// The audit feed and the log table render `module` and `type` straight from the
// API. Before this, the dashboard's Recent Activity card did
// `[{a.module.toUpperCase()}]`, which put "CLAIMS" in front of a French user on
// a screen that was otherwise fully localised — and did the same for every one
// of the ~18 module slugs the audit feed emits.
//
// Neither guard could see it: the S9 clean-screen test only checks that keys
// already inside t('...') resolve in en/fr/de, and the Python scanner only
// finds hardcoded English *literals*. A raw data slug is neither.
//
// Slugs are normalised to camelCase keys the same way statusLabel() in
// useDashboardFunctions.js does, so 'forgot-password' becomes
// logTypes.forgotPassword and no key ever contains a character that the
// clean-screen key scanner would choke on.

/** 'forgot-password' | 'FORGOT_PASSWORD' -> 'forgotPassword' */
export const slugToKey = (slug) =>
  String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/[-_\s]+(.)/g, (_, c) => c.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '');

/**
 * A label for an audit/activity MODULE ('claims', 'bordereaux', 'auth'…).
 *
 * The fallback is deliberately the raw slug rather than an English word: if a
 * new module ships without a key, a French user sees the untranslated token and
 * it looks like the bug it is, instead of silently reading as correct English
 * everywhere. `modulesCoverage` in the S9 test is what stops it reaching them.
 */
export const moduleLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`modules.${key}`, { defaultValue: String(slug).toUpperCase() });
};

/** A label for an audit log TYPE — an action ('create', 'login') or an entity ('claims'). */
export const logTypeLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`logTypes.${key}`, { defaultValue: String(slug) });
};

/**
 * A record STATUS ('reported', 'closed', 'active'…). Resolves against
 * common.status.*, the same namespace StatusBadge uses, so a status reads
 * identically wherever it appears — chip, table cell or activity line.
 */
export const statusLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`common.status.${key}`, { defaultValue: String(slug) });
};

/** log.logType — the outcome of a logged operation (none/success/fail/info). */
export const logStatusLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`logStatus.${key}`, { defaultValue: String(slug) });
};

/** One audit actionTag ('users', 'read', 'create'). */
export const tagLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`logTags.${key}`, { defaultValue: String(slug) });
};

/** A claim PRIORITY ('critical'…'low'). */
export const priorityLabel = (t, slug) => {
  const key = slugToKey(slug);
  if (!key) return '';
  return t(`common.priority.${key}`, { defaultValue: String(slug) });
};

/**
 * What an audit row DID, in the reader's language.
 *
 * The backend stores English prose — `action: 'Created'`,
 * `` description: `Created client ${name}` `` — which cannot be translated
 * after the fact. But the same row also carries STRUCTURED `method` and
 * `resource`, and those can be composed into a French sentence directly.
 *
 * So: compose when the structured fields are there, and fall back to the
 * stored prose when they are not — a row naming a specific entity keeps its
 * name rather than being flattened into a generic phrase. Returns null when
 * nothing can be composed, so the caller keeps its existing fallback chain.
 */
// Derived from actionTags, NOT from `method`. utils/logs.js never writes a
// method onto an audit row — only the caller-supplied resource, description
// and actionTags — so a method-based composition would have been dead code in
// production while looking correct in a harness. actionTags carry both halves:
// ['users','read'] and ['client','create'].
const VERB_TAG = {
  read: 'viewed', create: 'created', new: 'created',
  update: 'updated', edit: 'updated', status: 'updated', transition: 'updated',
  delete: 'deleted', deactivate: 'deactivated',
  download: 'downloaded', upload: 'uploaded',
  share: 'shared', copy: 'copied', move: 'moved',
  convert: 'converted', assign: 'assigned', validate: 'validated',
};
export const composedLogAction = (t, log) => {
  const tags = Array.isArray(log?.actionTags) ? log.actionTags.map(x => String(x).toLowerCase()) : [];
  let verb = null;
  let entity = null;
  for (const tag of tags) {
    if (!verb && VERB_TAG[tag]) verb = VERB_TAG[tag];
    else if (!entity) entity = tag;
  }
  if (!verb) return null;                       // keep the stored prose
  const target = entity ? tagLabel(t, entity) : (log?.resource ? moduleLabel(t, log.resource) : null);
  if (!target) return null;
  return t(`logActions.${verb}`, { target });
};
